import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Card, CardContent, Button, Grid,
  Chip, CircularProgress, TextField, Alert, FormControl,
  Select, MenuItem, InputLabel, Dialog, DialogTitle, DialogContent,
  IconButton, Stepper, Step, StepLabel, Divider, Stack
} from '@mui/material';
import { Close as CloseIcon, ArrowBack, ArrowForward } from '@mui/icons-material';
import { useKeycloak } from '@react-keycloak/web';
import { useTheme } from '@mui/material/styles';
import { useUser } from '../../../contexts/UserContext';
import { createAgent, getAvailableEndpoints, getAgentTemplates, getEndpointByNodeId } from '../../api/services/agent-studio/AgentManagement';
import { getModels } from '../../api/services/agent-studio/AgentFeatures';
import { getNodes, transformNodeToDisplayFormat, getNodeById } from '../../api/services/computeService/NodeService';
import { getClientNodes } from '../../api/services/computeService/ClientNodeService';
import { getDepartmentNodes } from '../../api/services/computeService/DepartmentNodeService';
import { getTeamNodes } from '../../api/services/computeService/TeamNodeService';
import { getUserNodes } from '../../api/services/computeService/UserNodeService';
import { 
  AccountBalance as AccountBalanceIcon,
  Gavel as GavelIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  SupportAgent as SupportAgentIcon,
  Business as BusinessIcon,
  Computer as ComputerIcon,
  Campaign as CampaignIcon,
  Extension as ExtensionIcon,
  Celebration as CelebrationIcon,
  Rocket as RocketIcon,
  Search as SearchIcon,
  Storage as StorageIcon,
  Build as BuildIcon,
  Info as InfoIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

  const iconMap = {
    AccountBalance: AccountBalanceIcon,
    Gavel: GavelIcon,
    People: PeopleIcon,
    AttachMoney: AttachMoneyIcon,
    SupportAgent: SupportAgentIcon,
    Business: BusinessIcon,
    Computer: ComputerIcon,
    Campaign: CampaignIcon
  };

const cleanPromptTemplate = (prompt) => {
  if (!prompt) return '';
  
  let cleaned = prompt;
  
  cleaned = cleaned.replace(/\{\{TASKS\}\}/gi, '');
  cleaned = cleaned.replace(/\{\{PERSONA\}\}/gi, '');
  cleaned = cleaned.replace(/\{\{AGENT_TYPE\}\}/gi, '');
  
  const lines = cleaned.split('\n');
  const filteredLines = [];
  let skipSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    
    if (/^\d+\.\s+[A-Z]/.test(trimmedLine)) {
      skipSection = true;
      continue;
    }
    
    if (skipSection) {
      if (/^-\s/.test(trimmedLine)) {
        continue;
      }
      if (trimmedLine === '' && (i === lines.length - 1 || lines[i + 1]?.trim() === '' || !/^-\s/.test(lines[i + 1]?.trim() || ''))) {
        skipSection = false;
        continue;
      }
      if (trimmedLine !== '' && !/^-\s/.test(trimmedLine) && !/^\d+\./.test(trimmedLine)) {
        skipSection = false;
      } else {
        continue;
      }
    }
    
    if (!skipSection && !/^-\s/.test(trimmedLine)) {
      filteredLines.push(line);
    }
  }
  
  cleaned = filteredLines.join('\n');
  
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  
  cleaned = cleaned.trim();
  
  return cleaned;
};

const extractErrorMessage = (error) => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error.message) return error.message;
  if (error.details) return error.details;
  if (error.code) return `Error ${error.code}: ${error.message || 'An error occurred'}`;
  return JSON.stringify(error);
};

const steps = ['Template Selection', 'Configuration', 'Model Selection'];

const AgentWizard = ({ open, onComplete, onCancel }) => {
  const { keycloak } = useKeycloak();
  const theme = useTheme();
  const { isSuperAdmin, user, userPermissions } = useUser();
  const [activeStep, setActiveStep] = useState(0);
  const [agentConfig, setAgentConfig] = useState({
    name: '',
    templateId: '',
    language: 'en',
    endpoint: '',
    model: '',
    models: [],
    systemPrompt: ''
  });

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templatesError, setTemplatesError] = useState(null);

  const [endpoints, setEndpoints] = useState([]);
  const [nodes, setNodes] = useState({});
  const [availableNodes, setAvailableNodes] = useState([]);
  const [loadingNodes, setLoadingNodes] = useState(false);
  const [loadingEndpoints, setLoadingEndpoints] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState(null);

  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentError, setDeploymentError] = useState(null);
  const [deploymentCompleted, setDeploymentCompleted] = useState(false);

  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setAgentConfig({
        name: '',
        templateId: '',
        language: 'en',
        endpoint: '',
        model: '',
        models: [],
        systemPrompt: ''
      });
      setDeploymentCompleted(false);
      setDeploymentError(null);
      setIsDeploying(false);
      setModelsError(null);
      setAvailableModels([]);
    }
  }, [open]);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!keycloak?.token) {
        setLoadingTemplates(false);
        setTemplatesError('Authentication required');
        return;
      }

      try {
        setLoadingTemplates(true);
        const response = await getAgentTemplates(keycloak.token);

        if (response.success && response.data.agents) {
          const templatesArray = Object.entries(response.data.agents).map(([key, value]) => ({
            id: key,
            name: value.name,
            description: value.description,
            prompt_template: cleanPromptTemplate(value.promptTemplate || ''),
            icon: value.icon
          }));

          setTemplates(templatesArray);
          setTemplatesError(null);

          if (templatesArray.length > 0) {
            handleTemplateSelect(templatesArray[0].id, templatesArray);
          }
        } else {
          setTemplatesError(extractErrorMessage(response.error) || 'Failed to load templates');
        }
      } catch (error) {
        setTemplatesError('Failed to load templates');
        console.error('Error fetching templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
    fetchNodes();
  }, [keycloak?.token, isSuperAdmin, userPermissions, user]);

  const fetchNodes = async () => {
    try {
      setLoadingNodes(true);
      await keycloak.updateToken(30);
      
      let nodeIds = [];

      if (isSuperAdmin) {
        const response = await getNodes({
          paginationParams: { page: 0, page_size: 200 },
          token: keycloak.token
        });
        
        if (response.success && response.data.nodes) {
          nodeIds = response.data.nodes.map(node => node.id || node.nodeId).filter(id => id);
        }
      } else if (userPermissions) {
        if (userPermissions.teamIds && userPermissions.teamIds.length > 0) {
          const response = await getTeamNodes({
            teamId: userPermissions.teamIds[0],
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        } else if (userPermissions.departmentIds && userPermissions.departmentIds.length > 0) {
          const response = await getDepartmentNodes({
            departmentId: userPermissions.departmentIds[0],
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        } else if (userPermissions.clientIds && userPermissions.clientIds.length > 0) {
          const response = await getClientNodes({
            clientId: userPermissions.clientIds[0],
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        } else if (user && user.id) {
          const response = await getUserNodes({
            userId: user.id,
            token: keycloak.token
          });
          if (response.success && response.data.nodeIds) {
            nodeIds = response.data.nodeIds;
          }
        }
      }

      if (nodeIds.length > 0) {
        const detailedNodes = await Promise.all(
          nodeIds.map(async (nodeId) => {
            try {
              const nodeResponse = await getNodeById(nodeId, keycloak.token);
              if (nodeResponse.success && nodeResponse.data) {
                return transformNodeToDisplayFormat(nodeResponse.data);
              }
              return null;
            } catch (error) {
              console.warn(`Failed to fetch details for node ${nodeId}:`, error);
              return null;
            }
          })
        );

        const transformedNodes = detailedNodes.filter(node => node !== null);
        setAvailableNodes(transformedNodes);

        if (transformedNodes.length > 0) {
          const defaultNode = transformedNodes[0];
          await handleNodeSelect(defaultNode);
        }
      } else {
        setAvailableNodes([]);
      }
    } catch (error) {
      console.error("Error fetching nodes:", error);
    } finally {
      setLoadingNodes(false);
    }
  };

  const handleNodeSelect = async (node) => {
    try {
      setLoadingEndpoints(true);
      await keycloak.updateToken(30);
      
      const endpointResponse = await getEndpointByNodeId(keycloak.token, node.id);
      
      if (endpointResponse.success && endpointResponse.data && endpointResponse.data.endpoint) {
        const endpoint = endpointResponse.data.endpoint;
        
        setAgentConfig(prev => ({
          ...prev,
          endpoint: endpoint,
          node_id: node.id,
          selectedNode: { ...node, endpointUrl: endpoint }
        }));

        setModelsError(null);
        await fetchModelsForEndpoint(endpoint);
      } else {
        console.error("Failed to get endpoint for node:", endpointResponse.error);
      }
    } catch (error) {
      console.error("Error fetching endpoint for node:", error);
    } finally {
      setLoadingEndpoints(false);
    }
  };

  const fetchModelsForEndpoint = async (endpoint) => {
    try {
      setLoadingModels(true);
      setModelsError(null);
      await keycloak.updateToken(30);
      
      const normalizeEndpoint = (url) => {
        if (!url) return '';
        return url.trim().replace(/^https?:\/\//, '').toLowerCase();
      };
      
      const endpointToUse = endpoint ? normalizeEndpoint(endpoint) : null;
      
      const response = await getModels(keycloak.token, endpointToUse);
      if (response && response.data) {
        const modelList = response.data.map(model => ({
          id: model.key,
          name: model.key.split('/').pop(),
          value: model.key,
          provider: model.key.split('/')[0]
        }));
        setAvailableModels(modelList);
        setModelsError(null);

        if (modelList.length > 0) {
          setAgentConfig(prev => ({
            ...prev,
            model: modelList[0].value,
            models: [modelList[0].value]
          }));
        } else {
          setModelsError('No models available for this endpoint');
        }
      } else {
        setModelsError('Failed to fetch models. Please try again.');
        setAvailableModels([]);
      }
    } catch (error) {
      console.error("Error fetching models for endpoint:", error);
      setModelsError(error.message || 'Failed to fetch models. Please try again.');
      setAvailableModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleTemplateSelect = (templateId, templatesArr = templates) => {
    if (templateId === 'custom') {
      setAgentConfig(prev => ({
        ...prev,
        name: '',
        templateId: 'custom',
        systemPrompt: ''
      }));
      return;
    }
    const template = (templatesArr.length ? templatesArr : templates).find(t => t.id === templateId);
    if (template) {
      setAgentConfig(prev => {
        const isFirstSelection = !prev.templateId || prev.templateId === '';
        const nameWasFromTemplate = prev.name === templates.find(t => t.id === prev.templateId)?.name;
        
        return {
          ...prev,
          name: (isFirstSelection || nameWasFromTemplate) ? template.name : prev.name,
          templateId: templateId,
          systemPrompt: template.prompt_template || ''
        };
      });
    }
  };

  const handleNodeChange = async (node) => {
    await handleNodeSelect(node);
  };

  const handleModelToggle = (modelId) => {
    setAgentConfig(prev => {
      if (prev.models.includes(modelId)) {
        const newState = {
          ...prev,
          models: prev.models.filter(id => id !== modelId)
        };

        if (prev.model === modelId) {
          newState.model = newState.models.length > 0 ? newState.models[0] : '';
        }

        return newState;
      } else {
        const newModels = [...prev.models, modelId];
        return {
          ...prev,
          models: newModels,
          model: prev.model || modelId
        };
      }
    });
  };

  const handleModelSelect = (modelId) => {
    setAgentConfig(prev => ({
      ...prev,
      model: modelId
    }));
  };

  const getGpuInfo = (gpu) => {
    if (!gpu) return { vendor: 'GPU_VENDOR_NVIDIA', model: 'GPU_MODEL_A100' };

    const gpuLower = gpu.toLowerCase();
    if (gpuLower.includes('mi210')) {
      return { vendor: 'GPU_VENDOR_AMD', model: 'GPU_MODEL_MI210' };
    } else if (gpuLower.includes('mi250')) {
      return { vendor: 'GPU_VENDOR_AMD', model: 'GPU_MODEL_MI250' };
    } else if (gpuLower.includes('mi300') || gpuLower.includes('mi325')) {
      return { vendor: 'GPU_VENDOR_AMD', model: 'GPU_MODEL_MI300' };
    } else if (gpuLower.includes('h100')) {
      return { vendor: 'GPU_VENDOR_NVIDIA', model: 'GPU_MODEL_H100' };
    } else if (gpuLower.includes('a100')) {
      return { vendor: 'GPU_VENDOR_NVIDIA', model: 'GPU_MODEL_A100' };
    } else if (gpuLower.includes('v100')) {
      return { vendor: 'GPU_VENDOR_NVIDIA', model: 'GPU_MODEL_V100' };
    }
    return { vendor: 'GPU_VENDOR_NVIDIA', model: 'GPU_MODEL_A100' };
  };

  const handleDeploy = async () => {
    setIsDeploying(true);
    setDeploymentError(null);

    try {
      await keycloak.updateToken(30);
      const token = keycloak.token;

      if (!agentConfig.templateId) {
        throw new Error("Please select a template");
      }

      if (!agentConfig.name) {
        throw new Error("Please enter a name for your agent");
      }

      const selectedNode = agentConfig.selectedNode;
      if (!selectedNode) {
        throw new Error("Node information not available for selected endpoint");
      }

      const gpuInfo = selectedNode ? getGpuInfo(selectedNode.gpu) : { vendor: 'GPU_VENDOR_NVIDIA', model: 'GPU_MODEL_A100' };
      
      const normalizeEndpoint = (url) => {
        if (!url) return '';
        return url.trim().replace(/^https?:\/\//, '').toLowerCase();
      };
      
      const nodeId = agentConfig.node_id || "4c1d4bc0-f79f-4315-8acb-9bc7cd5de2e1";

      const agentData = {
        name: agentConfig.name,
        node_id: nodeId,
        is_active: true,
        endpoint: agentConfig.endpoint ? agentConfig.endpoint.trim().replace(/^https?:\/\//, '') : '',
        model: agentConfig.model || (agentConfig.models && agentConfig.models.length > 0 ? agentConfig.models[0] : ''),
        models: agentConfig.models || [],
        language: agentConfig.language || 'en',
        audio_input_enabled: false,
        speech_model: "openai/whisper-large-v3",
        gpu_vendor: gpuInfo.vendor,
        gpu_model: gpuInfo.model,
        num_of_gpus: selectedNode?.noOfGpu || 1
      };

      if (agentConfig.systemPrompt) {
        agentData.system_prompt = agentConfig.systemPrompt;
      }

      if (!agentData.endpoint) {
        throw new Error("Please select a deployment endpoint");
      }

      if (!agentData.models || agentData.models.length === 0) {
        throw new Error("Please select at least one model");
      }

      const response = await createAgent(token, agentData);

      if (response.success) {
        setAgentConfig(prev => ({
          ...prev,
          apiResponse: response.data
        }));
        setDeploymentCompleted(true);
      } else {
        setDeploymentError(extractErrorMessage(response.error) || "Failed to create agent");
      }
    } catch (error) {
      console.error("Agent deployment error:", error);
      setDeploymentError(error.message || "An unexpected error occurred");
    } finally {
      setIsDeploying(false);
    }
  };

  const handleFinish = () => {
    onComplete(agentConfig);
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!agentConfig.templateId) {
        setDeploymentError('Please select a template or choose Custom Agent');
        return;
      }
    } else if (activeStep === 1) {
      if (!agentConfig.name) {
        setDeploymentError('Please enter a name for your agent');
        return;
      }
      if (!agentConfig.systemPrompt && agentConfig.templateId === 'custom') {
        setDeploymentError('Please enter a system prompt for your custom agent');
        return;
      }
      if (!agentConfig.node_id) {
        setDeploymentError('Please select a node');
        return;
      }
      if (!agentConfig.endpoint) {
        setDeploymentError('Endpoint not available for selected node');
        return;
      }
      if (agentConfig.endpoint && (!availableModels.length || loadingModels)) {
        await fetchModelsForEndpoint(agentConfig.endpoint);
      }
      if (modelsError) {
        setDeploymentError('Failed to load models. Please select a different node or try again.');
        return;
      }
      if (!availableModels.length && !loadingModels) {
        setDeploymentError('No models available for the selected endpoint. Please select a different node.');
        return;
      }
    }
    setDeploymentError(null);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setDeploymentError(null);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderStep1 = () => (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>Select Template</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose a pre-built template or create a custom agent. All agents include web search, RAG, and tool use capabilities.
      </Typography>

      <Card sx={{ mb: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(80, 200, 120, 0.1)' : 'rgba(80, 200, 120, 0.08)' }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon fontSize="small" />
            Included Capabilities
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <SearchIcon fontSize="small" color="primary" sx={{ mt: 0.5, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Web Search</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Access to real-time web information
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <StorageIcon fontSize="small" color="primary" sx={{ mt: 0.5, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>RAG (Retrieval-Augmented Generation)</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Document search and knowledge retrieval from uploaded files
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <BuildIcon fontSize="small" color="primary" sx={{ mt: 0.5, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Tool Use</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Ability to execute actions and interact with external systems
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {loadingTemplates && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>Loading templates...</Typography>
        </Box>
      )}

      {templatesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {templatesError}
        </Alert>
      )}

      {!loadingTemplates && !templatesError && (
        <Grid container spacing={2}>
          {templates.map((template) => {
            const IconComponent = iconMap[template.icon];
            return (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: agentConfig.templateId === template.id ? '2px solid' : '1px solid',
                    borderColor: agentConfig.templateId === template.id ? 'primary.main' : 'divider',
                    transition: 'all 0.2s',
                    minHeight: 180,
                    boxShadow: agentConfig.templateId === template.id ? 4 : 1
                  }}
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {IconComponent && <IconComponent sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />}
                      <Typography variant="subtitle1" fontWeight={600}>{template.name}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {template.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                cursor: 'pointer',
                border: agentConfig.templateId === 'custom' ? '2px solid' : '1px dashed',
                borderColor: agentConfig.templateId === 'custom' ? 'primary.main' : 'divider',
                transition: 'all 0.2s',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 180
              }}
              onClick={() => handleTemplateSelect('custom')}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <ExtensionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="subtitle1" fontWeight={600}>Custom Agent</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Create your own custom agent
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>Configure Agent</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Set up your agent's name, prompt, language, and deployment preferences.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Agent Name"
          value={agentConfig.name}
          onChange={(e) => setAgentConfig(prev => ({ ...prev, name: e.target.value }))}
          required
          error={!agentConfig.name}
          helperText={!agentConfig.name ? "Please enter a name for your agent" : ""}
          placeholder="Enter a descriptive name for your agent"
        />
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Language</InputLabel>
            <Select
              value={agentConfig.language}
              label="Language"
              onChange={(e) => setAgentConfig(prev => ({ ...prev, language: e.target.value }))}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="de">German</MenuItem>
              <MenuItem value="es">Spanish</MenuItem>
              <MenuItem value="fr">French</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="System Prompt"
          value={agentConfig.systemPrompt}
          onChange={(e) => setAgentConfig(prev => ({ ...prev, systemPrompt: e.target.value }))}
          multiline
          rows={8}
          placeholder="You are a helpful assistant that..."
          helperText="Define the system prompt for your agent. This determines how your agent behaves and responds."
          required={agentConfig.templateId === 'custom'}
          error={agentConfig.templateId === 'custom' && !agentConfig.systemPrompt}
        />
      </Box>

      {
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Select Node</Typography>
          {loadingNodes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {availableNodes.map((node) => {
                const isSelected = agentConfig.node_id === node.id;

                const getStatusChip = () => {
                  if (node.status === 'Active') {
                    return (
                      <Chip
                        label="Active"
                        size="small"
                        variant="outlined"
                        sx={{ 
                          borderColor: 'text.secondary',
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          height: '20px'
                        }}
                      />
                    );
                  }
                  return (
                    <Chip
                      label={node.status || "Unknown"}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        borderColor: 'text.secondary',
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                        height: '20px'
                      }}
                    />
                  );
                };

                const MetricItem = ({ icon, label, value }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                      {icon}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                        {label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', lineHeight: 1.2 }}>
                        {value}
                      </Typography>
                    </Box>
                  </Box>
                );

                return (
                  <Grid item xs={12} sm={6} md={4} key={node.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: 'background.paper',
                        border: isSelected ? `2px solid ${theme.palette.text.primary}` : `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: theme.palette.text.secondary,
                          boxShadow: 1
                        }
                      }}
                      onClick={() => handleNodeChange(node)}
                    >
                      <CardContent sx={{ flex: 1, p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getStatusChip()}
                          </Box>
                          {node.location && (
                            <Chip
                              icon={<LocationIcon fontSize="small" sx={{ fontSize: '0.75rem' }} />}
                              label={node.location}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                                height: '20px',
                                borderColor: 'text.secondary',
                                color: 'text.secondary',
                                '& .MuiChip-icon': {
                                  color: 'text.secondary'
                                }
                              }}
                            />
                          )}
                        </Box>

                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.875rem' }}>
                          {node.hostname || `Node ${node.id?.substring(0, 8)}`}
                        </Typography>
                        
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: '0.7rem' }}>
                          {node.ipAddress || 'No IP'} • {node.id?.substring(0, 8)}...
                        </Typography>

                        <Divider sx={{ my: 1 }} />

                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <MetricItem 
                              icon={<SpeedIcon sx={{ fontSize: '0.875rem' }} />}
                              label="GPUs"
                              value={node.numGpus || 0}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <MetricItem 
                              icon={<ComputerIcon sx={{ fontSize: '0.875rem' }} />}
                              label="CPUs"
                              value={node.numCpus || node.cpus || 0}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <MetricItem 
                              icon={<MemoryIcon sx={{ fontSize: '0.875rem' }} />}
                              label="Memory"
                              value={`${node.memory || 0} GB`}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <MetricItem 
                              icon={<StorageIcon sx={{ fontSize: '0.875rem' }} />}
                              label="Storage"
                              value={(() => {
                                const storageGB = node.storage || 0;
                                if (storageGB >= 1000) {
                                  return `${(storageGB / 1000).toFixed(1)} TB`;
                                }
                                return `${storageGB} GB`;
                              })()}
                            />
                          </Grid>
                        </Grid>

                        {(node.gpuBreakdown?.length > 0 || node.cpuModel) && (
                          <Box sx={{ mt: 1 }}>
                            <Stack spacing={0.5}>
                              {node.gpuBreakdown?.length > 0 && (
                                <>
                                  {node.gpuBreakdown.map((gpu, index) => (
                                    <Chip 
                                      key={index}
                                      label={`GPU: ${gpu.model} ${gpu.count > 1 ? `×${gpu.count}` : ''}`} 
                                      size="small" 
                                      variant="outlined"
                                      sx={{ 
                                        width: '100%', 
                                        justifyContent: 'flex-start',
                                        fontSize: '0.7rem',
                                        height: '22px',
                                        borderColor: 'text.secondary',
                                        color: 'text.secondary'
                                      }}
                                      icon={<SpeedIcon sx={{ fontSize: '0.75rem' }} />}
                                    />
                                  ))}
                                </>
                              )}
                              {node.cpuModel && (
                                <Chip 
                                  label={`CPU: ${node.cpuModel}`} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ 
                                    width: '100%', 
                                    justifyContent: 'flex-start',
                                    fontSize: '0.7rem',
                                    height: '22px',
                                    borderColor: 'text.secondary',
                                    color: 'text.secondary'
                                  }}
                                  icon={<ComputerIcon sx={{ fontSize: '0.75rem' }} />}
                                />
                              )}
                            </Stack>
                          </Box>
                        )}

                        {agentConfig.endpoint && isSelected && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                              Endpoint: {agentConfig.endpoint}
                            </Typography>
                          </Box>
                        )}
                        {loadingEndpoints && isSelected && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <CircularProgress size={14} sx={{ mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              Fetching endpoint...
                            </Typography>
                          </Box>
                        )}
                        {loadingModels && isSelected && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <CircularProgress size={14} sx={{ mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              Loading models...
                            </Typography>
                          </Box>
                        )}
                        {modelsError && isSelected && (
                          <Box sx={{ mt: 1 }}>
                            <Alert severity="error" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                              {modelsError}
                            </Alert>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              {!loadingNodes && availableNodes.length === 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    No nodes available. Please try again later.
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
          {modelsError && agentConfig.node_id && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {modelsError}
            </Alert>
          )}
        </Box>
      }
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>Select Models</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose one or more models for your agent. The first selected model will be the default.
      </Typography>

      {loadingModels ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : availableModels.length > 0 ? (
        <Grid container spacing={2}>
          {availableModels.map((model) => {
            const isSelected = agentConfig.models.includes(model.id);
            const isDefault = agentConfig.model === model.id;

            return (
              <Grid item xs={12} sm={6} md={4} key={model.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: isSelected ? '2px solid' : '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onClick={() => handleModelToggle(model.id)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle1">{model.name}</Typography>
                      <Chip
                        label={model.provider}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    {isSelected && (
                      <Box sx={{ mt: 1 }}>
                        {isDefault ? (
                          <Chip label="Default Model" size="small" color="primary" />
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModelSelect(model.id);
                            }}
                          >
                            Set as Default
                          </Button>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Alert severity="info">
          No models available for this endpoint. Please select a different endpoint.
        </Alert>
      )}
    </Box>
  );

  const renderContent = () => {
    if (isDeploying) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 1 }}>
            Deploying your AI Agent...
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            This may take a few moments while we configure your agent
          </Typography>
          {deploymentError && (
            <Alert severity="error" sx={{ mt: 2, mx: 'auto', maxWidth: 600 }}>
              {deploymentError}
            </Alert>
          )}
        </Box>
      );
    }

    if (deploymentCompleted) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CelebrationIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
          <Typography variant="h4" gutterBottom>Deployment Successful!</Typography>
          <Typography variant="body1" sx={{ mb: 4 }}>
            Your AI agent "{agentConfig.name}" has been successfully deployed and is now online.
          </Typography>
          <Card sx={{ mb: 4, p: 3, maxWidth: 600, mx: 'auto', textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom>Deployment Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Agent ID:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.id || '-'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Agent Name:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.name || '-'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Endpoint:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.endpoint || '-'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="body2" color="text.secondary">Active:</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{agentConfig.apiResponse?.agent?.isActive ? 'Yes' : 'No'}</Typography></Grid>
            </Grid>
          </Card>
          <Button
            variant="contained"
            size="large"
            onClick={handleFinish}
            sx={{ px: 4 }}
          >
            Close
          </Button>
        </Box>
      );
    }

    return (
      <Box>
        <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {deploymentError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {deploymentError}
          </Alert>
        )}

        <Box sx={{ minHeight: 400 }}>
          {activeStep === 0 && renderStep1()}
          {activeStep === 1 && renderStep2()}
          {activeStep === 2 && renderStep3()}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="outlined"
            onClick={activeStep === 0 ? onCancel : handleBack}
            startIcon={<ArrowBack />}
            sx={{ minWidth: 120 }}
          >
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Button
            variant="contained"
            onClick={activeStep === steps.length - 1 ? handleDeploy : handleNext}
            endIcon={activeStep === steps.length - 1 ? <RocketIcon /> : <ArrowForward />}
            disabled={
              activeStep === steps.length - 1 &&
              (!agentConfig.name ||
                !agentConfig.templateId ||
                !agentConfig.endpoint ||
                !agentConfig.node_id ||
                !agentConfig.model ||
                agentConfig.models.length === 0)
            }
            sx={{ minWidth: 120 }}
          >
            {activeStep === steps.length - 1 ? 'Deploy Agent' : 'Next'}
          </Button>
        </Box>
      </Box>
    );
};

  const handleClose = () => {
    if (!isDeploying && !deploymentCompleted) {
      onCancel();
    } else if (deploymentCompleted) {
      handleFinish();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
                          fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          height: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6">Create AI Agent</Typography>
      {!isDeploying && !deploymentCompleted && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ overflow: 'auto' }}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default AgentWizard;